/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { kebabCase } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonBase, Tooltip, Typography } from '@mui/material';

import { Namespaces } from '../../i18n';
import { ProductStatus } from '../../types/product/ProductStatus';
import { createProductsListStyles } from './ProductsList.styles';

type ProductsListItemProps = {
  className?: string;
  onClick?: () => any;
  icon?: React.ReactElement<any>;
  name: string;
  products: ProductStatus;
  noItemsText: string;
};

export function ProductsListItem(props: ProductsListItemProps) {
  const { t } = useTranslation(Namespaces.OrganizationAdmin);
  const { className = '', onClick, icon, name, products, noItemsText } = props;
  const classes = createProductsListStyles();

  const { up, down } = useMemo(
    () => ({
      up: products.Running + products.Updating + products.Importing,
      down: products.Unavailable + products.Error,
    }),
    [products],
  );

  const total = useMemo(() => up + down, [up, down]);

  return (
    <div className={classes.card}>
      <div>
        {!total ? (
          <div className={classes.cardRow}>
            <Typography className={classes.cardRowTitle}>{noItemsText}</Typography>
          </div>
        ) : (
          <div className={classes.cardRow}>
            <Typography className={classes.cardRowTitle}>{t('general:services.title')}</Typography>
            <div className={classes.cardProductRow}>
              <div className={classes.cardCell}>
                <span className={classes.cardRowValue}>{total}</span>
                <span className={classes.cardRowKey}>{t('departments.products.total')}</span>
              </div>
              <div className={classes.cardCell}>
                <span className={classes.cardRowValue}>{up}</span>
                <span className={classes.cardRowKey}>{t('departments.products.up')}</span>
              </div>
              <div className={classes.cardCell}>
                <span className={classes.cardRowValue}>{down}</span>
                <span className={classes.cardRowKey}>{t('departments.products.down')}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <ButtonBase
        title={name}
        className={className}
        classes={{ root: classes.itemRoot }}
        onClick={onClick}
        data-cy={`box-${kebabCase(name)}`}
      >
        {icon ? <div className={classes.itemIcon}>{icon}</div> : null}
        <Tooltip title={name} disableHoverListener={name.length < 16}>
          <div className={classes.itemText}>{name}</div>
        </Tooltip>
        <div className={classes.itemIconArrow} />
      </ButtonBase>
    </div>
  );
}
